import { useEffect, useState } from "react";
import { AdminLayout } from "../../components/Layouts";
import { api, readUser, errMsg } from "../../lib/api";

const WALLET_RE = /^0x[a-fA-F0-9]{40}$/;
// Index matches JS getDay() (0=Sun..6=Sat) — Turkish labels for admin panel
const DAY_LABELS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const DAY_FULL = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

export default function AdminWalletSettings() {
  const user = readUser();
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState({ ok: false, msg: "" });
  const [rpc, setRpc] = useState(null);
  const [testing, setTesting] = useState(false);

  async function load() { const r = await api.get("/admin/settings"); setSettings(r.data); }
  useEffect(() => { load().catch(() => {}); }, []);

  async function save(e) {
    e.preventDefault();
    setNotice({ ok: false, msg: "" });
    const trimmedWallet = (settings.deposit_wallet || "").trim();
    if (trimmedWallet && !WALLET_RE.test(trimmedWallet)) {
      setNotice({ ok: false, msg: "Depozito cüzdanı 0x ile başlayan 42 karakterlik BEP20 adresi olmalıdır." });
      return;
    }
    if (settings.usdt_contract && !WALLET_RE.test(settings.usdt_contract.trim())) {
      setNotice({ ok: false, msg: "USDT contract adresi 0x ile başlayan 42 karakterlik adres olmalıdır." });
      return;
    }
    // Business rule: a valid wallet promotes the platform to LIVE automatically.
    const nextMode = WALLET_RE.test(trimmedWallet) ? "LIVE" : "SETUP";
    try {
      const payload = {
        ...settings,
        mode: nextMode,
        deposit_wallet: trimmedWallet,
        usdt_contract: (settings.usdt_contract || "").trim(),
        required_confirmations: Number(settings.required_confirmations),
        minimum_deposit: Number(settings.minimum_deposit),
        minimum_withdrawal: Number(settings.minimum_withdrawal),
        withdrawal_fee: Number(settings.withdrawal_fee),
        max_daily_withdrawal: Number(settings.max_daily_withdrawal || 0),
        referral_rates: (settings.referral_rates || []).map(Number),
        withdrawal_days: (settings.withdrawal_days || [0,1,2,3,4,5,6]).map(Number),
      };
      const r = await api.put("/admin/settings", payload);
      setSettings(r.data);
      const live = r.data.mode === "LIVE";
      setNotice({ ok: true, msg: live ? "LIVE moda geçildi. On-chain doğrulama aktif." : "Kaydedildi. (Cüzdan boş olduğundan SETUP modunda.)" });
    } catch (e) { setNotice({ ok: false, msg: errMsg(e) }); }
  }

  async function testRpc() {
    setRpc(null); setTesting(true);
    try {
      const r = await api.post("/admin/wallet/test");
      setRpc({ ok: true, msg: `Bağlantı başarılı · Blok #${r.data.block_number}` });
    } catch (e) { setRpc({ ok: false, msg: errMsg(e) }); }
    finally { setTesting(false); }
  }

  async function toggleMode() {
    const target = settings.mode === "LIVE" ? "SETUP" : "LIVE";
    if (target === "LIVE" && !WALLET_RE.test((settings.deposit_wallet || "").trim())) {
      setNotice({ ok: false, msg: "LIVE moda geçmek için önce geçerli bir depozito cüzdanı girip kaydedin." });
      return;
    }
    try {
      const payload = { ...settings, mode: target, required_confirmations: Number(settings.required_confirmations), minimum_deposit: Number(settings.minimum_deposit), minimum_withdrawal: Number(settings.minimum_withdrawal), withdrawal_fee: Number(settings.withdrawal_fee), max_daily_withdrawal: Number(settings.max_daily_withdrawal || 0), referral_rates: (settings.referral_rates || []).map(Number), withdrawal_days: (settings.withdrawal_days || [0,1,2,3,4,5,6]).map(Number) };
      const r = await api.put("/admin/settings", payload);
      setSettings(r.data);
      setNotice({ ok: true, msg: `Mod güncellendi: ${r.data.mode}` });
    } catch (e) { setNotice({ ok: false, msg: errMsg(e) }); }
  }

  if (!settings) return <AdminLayout user={user} active="Cüzdan Ayarları"><div className="empty-activity"><p>Yükleniyor…</p></div></AdminLayout>;

  const walletValid = WALLET_RE.test((settings.deposit_wallet || "").trim());
  return (
    <AdminLayout user={user} active="Cüzdan Ayarları">
      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">BEP20 USDT</span><h2>Cüzdan ve Doğrulama</h2></div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span className={`status ${settings.mode === "LIVE" ? "confirmed" : "pending"}`} data-testid="admin-mode-indicator">{settings.mode}</span>
            <button className="btn btn-light" onClick={toggleMode} data-testid="admin-mode-toggle">
              {settings.mode === "LIVE" ? "SETUP'a geç" : "LIVE'a geç"}
            </button>
          </div>
        </div>
        <p className="note">BEP20 cüzdan adresi boş olduğunda sistem otomatik olarak <strong>SETUP</strong> moduna geçer; gerçek para kabul edilmez. Geçerli bir adres girip kaydettiğinizde platform <strong>LIVE</strong> moduna alınır ve on-chain doğrulama devreye girer.</p>
        {notice.msg && <div className={notice.ok ? "success" : "error"} data-testid="admin-wallet-notice">{notice.msg}</div>}

        <form onSubmit={save} className="pkg-form">
          <label className="full-row">BEP20 USDT Depozito Cüzdanı
            <input data-testid="admin-deposit-wallet" value={settings.deposit_wallet} onChange={(e) => setSettings({ ...settings, deposit_wallet: e.target.value })} placeholder="0x…" />
            {settings.deposit_wallet && (
              <small data-testid="wallet-validity" style={{ color: walletValid ? "#1a8b5e" : "#be4a5c", fontSize: 11 }}>
                {walletValid ? "✓ Geçerli BEP20 adresi" : "✗ 42 karakter 0x adresi değil"}
              </small>
            )}
          </label>
          <label className="full-row">USDT Contract Adresi<input data-testid="admin-usdt-contract" value={settings.usdt_contract} onChange={(e) => setSettings({ ...settings, usdt_contract: e.target.value })} /></label>
          <label>Gerekli Onay Sayısı<input data-testid="admin-confirmations" type="number" value={settings.required_confirmations} onChange={(e) => setSettings({ ...settings, required_confirmations: e.target.value })} /></label>
          <label>Minimum Depozito ($)<input data-testid="admin-min-deposit" type="number" value={settings.minimum_deposit} onChange={(e) => setSettings({ ...settings, minimum_deposit: e.target.value })} /></label>
          <label>Minimum Çekim ($)<input data-testid="admin-min-withdrawal" type="number" value={settings.minimum_withdrawal} onChange={(e) => setSettings({ ...settings, minimum_withdrawal: e.target.value })} /></label>
          <label>Çekim Ücreti (%)<input data-testid="admin-withdrawal-fee" type="number" min="0" max="100" step="0.01" value={settings.withdrawal_fee} onChange={(e) => setSettings({ ...settings, withdrawal_fee: e.target.value, withdrawal_fee_mode: "percentage" })} /></label>
          <label>Günlük Maks. Çekim ($) <span className="optional">0 = sınırsız</span>
            <input data-testid="admin-max-daily-withdrawal" type="number" min="0" step="0.01" value={settings.max_daily_withdrawal ?? 0} onChange={(e) => setSettings({ ...settings, max_daily_withdrawal: e.target.value })} />
          </label>
          <div className="full-row withdrawal-days-field" data-testid="admin-withdrawal-days-field">
            <span className="field-label">Para Çekim Günleri</span>
            <small className="field-hint">Seçilen günlerde kullanıcılar çekim talebi oluşturabilir. Tüm günler açıksa kısıtlama yoktur.</small>
            <div className="day-pills">
              {DAY_LABELS.map((lbl, idx) => {
                const active = (settings.withdrawal_days || [0,1,2,3,4,5,6]).map(Number).includes(idx);
                return (
                  <button
                    type="button"
                    key={idx}
                    className={`day-pill ${active ? "on" : ""}`}
                    data-testid={`admin-withdrawal-day-${idx}`}
                    aria-pressed={active}
                    title={DAY_FULL[idx]}
                    onClick={() => {
                      const current = new Set((settings.withdrawal_days || [0,1,2,3,4,5,6]).map(Number));
                      if (current.has(idx)) current.delete(idx); else current.add(idx);
                      setSettings({ ...settings, withdrawal_days: Array.from(current).sort((a,b)=>a-b) });
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
          <button className="btn btn-accent" data-testid="admin-wallet-save">Ayarları Kaydet</button>
        </form>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">BAĞLANTI TESTİ</span><h2>BSC RPC Durumu</h2></div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-light" onClick={async () => { setRpc(null); try { const r = await api.post("/admin/monitor/run"); setRpc({ ok: true, msg: `Monitor çalıştı — eşleşen: ${r.data.matched || 0}, doğrulanan: ${r.data.verified || 0}` }); } catch (e) { setRpc({ ok: false, msg: errMsg(e) }); } }} data-testid="admin-monitor-run">Monitor'ü Şimdi Çalıştır</button>
            <button className="btn btn-accent" onClick={testRpc} disabled={testing} data-testid="admin-rpc-test">
              {testing ? "Test ediliyor…" : "RPC Bağlantısını Test Et"}
            </button>
          </div>
        </div>
        <p className="note">Zincir izleyici her 5 dakikada bir çalışır. Beklemeyen zincir onayını hızlandırmak için yukarıdaki "Monitor'ü Şimdi Çalıştır" düğmesini kullanabilirsiniz. RPC testi sadece bağlantıyı doğrular — fon göndermez.</p>
        {rpc && <div className={rpc.ok ? "success" : "error"} data-testid="admin-rpc-notice">{rpc.msg}</div>}
      </section>
    </AdminLayout>
  );
}
